import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { EDITOR_ROLES } from "@shared/core/types";
import { LINK_DIRECTION_LABELS, type LinkDirection, type ShareLink } from "@shared/modules/x-avocats";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { api } from "../../../core/lib/api";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Badge } from "../../../core/components/ui/badge";
import { Modal } from "../../../core/components/ui/modal";
import { Field, Input, Select, Textarea } from "../../../core/components/ui/input";
import { Icon } from "../../../core/components/ui/icon";
import { formatDateTime } from "../../../core/lib/format";
import { allowedFor } from "../../../core/modules";
import { moduleEnabled } from "../../../core/blueprint";
import { createLink, listLinks, revokeLink } from "../lib/api";

function stateOf(l: ShareLink): { label: string; variant: "success" | "neutral" | "danger" | "warning" } {
  if (l.revokedAt) return { label: "Révoqué", variant: "danger" };
  if (l.consumedAt) return { label: "Consommé", variant: "neutral" };
  if (new Date(l.expiresAt).getTime() < Date.now()) return { label: "Expiré", variant: "warning" };
  return { label: "Actif", variant: "success" };
}

export function CandidateLinksPage() {
  const { id = "" } = useParams();
  const user = useAuthStore((s) => s.user);
  const push = useToastStore((s) => s.push);
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [docs, setDocs] = useState<{ id: string; originalName: string; typeCode: string }[]>([]);
  const [direction, setDirection] = useState<LinkDirection>("telechargement");
  const [email, setEmail] = useState("");
  const [hours, setHours] = useState("72");
  const [maxUses, setMaxUses] = useState("1");
  const [message, setMessage] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [created, setCreated] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canEdit = allowedFor({ roles: EDITOR_ROLES }, user?.role);

  const load = useCallback(async () => {
    setLinks(await listLinks(id).catch(() => []));
    if (moduleEnabled("x-documents")) {
      const r = await api<{ documents: { id: string; originalName: string; typeCode: string }[] }>(`/api/documents/candidat/${id}`).catch(() => null);
      setDocs(r?.documents ?? []);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await createLink({ candidateId: id, direction, documentIds: direction === "telechargement" ? selected : [], email, hours: Number(hours), maxUses: Number(maxUses), message: message || null });
      setCreated(r.url);
      setEmail("");
      setSelected([]);
      setMessage("");
      await load();
    } catch {
      push("danger", "Création impossible : vérifiez le courriel et les pièces choisies.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Liens sécurisés"
        subtitle="Récupération ou dépôt de documents par un avocat, avec code par courriel et expiration."
        actions={
          <Link className="btn btn--ghost" to={`/candidats/${id}`}>
            <span className="btn__icon">
              <Icon name="chevron-left" size={16} />
            </span>
            Retour à la fiche
          </Link>
        }
      />

      {canEdit ? (
        <Card title="Nouveau lien">
          <div className="form-grid">
            <Field label="Sens">
              <Select value={direction} onChange={(e) => setDirection(e.target.value as LinkDirection)}>
                <option value="telechargement">{LINK_DIRECTION_LABELS.telechargement}</option>
                <option value="depot">{LINK_DIRECTION_LABELS.depot}</option>
              </Select>
            </Field>
            <Field label="Courriel du destinataire" hint="Le code de vérification y sera envoyé.">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </Field>
            <Field label="Validité (heures)">
              <Input type="number" min={1} max={720} value={hours} onChange={(e) => setHours(e.target.value)} />
            </Field>
            <Field label={direction === "telechargement" ? "Téléchargements par pièce" : "Dépôts autorisés"} hint="1 = le lien expire après usage.">
              <Input type="number" min={1} max={10} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
            </Field>
            <div className="form-grid__full">
              <Field label="Message au destinataire (facultatif)">
                <Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} />
              </Field>
            </div>
            {direction === "telechargement" ? (
              <div className="form-grid__full liens__docs">
                <span className="field__label">Pièces à partager</span>
                {docs.map((d) => (
                  <label key={d.id} className="liens__doc">
                    <input type="checkbox" checked={selected.includes(d.id)} onChange={(e) => setSelected(e.target.checked ? [...selected, d.id] : selected.filter((x) => x !== d.id))} />
                    {d.originalName} <span className="muted">· {d.typeCode}</span>
                  </label>
                ))}
                {docs.length === 0 ? <p className="muted">Aucune pièce dans ce dossier.</p> : null}
              </div>
            ) : null}
          </div>
          <div className="liens__actions">
            <Button variant="primary" disabled={busy || !email || (direction === "telechargement" && selected.length === 0)} onClick={submit} icon={<Icon name="shield" size={16} />}>
              Créer le lien
            </Button>
          </div>
        </Card>
      ) : null}

      <table className="table">
        <thead>
          <tr>
            <th>Sens</th>
            <th>Destinataire</th>
            <th>État</th>
            <th className="table__col--wide">Expire</th>
            <th className="table__col--wide">Usages</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {links.map((l) => {
            const s = stateOf(l);
            return (
              <tr key={l.id}>
                <td>{LINK_DIRECTION_LABELS[l.direction]}</td>
                <td>{l.email}</td>
                <td>
                  <Badge variant={s.variant}>{s.label}</Badge>
                </td>
                <td className="table__col--wide">{formatDateTime(l.expiresAt, { dateStyle: "short", timeStyle: "short" })}</td>
                <td className="table__col--wide">
                  {l.uses} / {l.maxUses * Math.max(1, l.documentIds.length)}
                </td>
                <td className="table__actions">
                  {canEdit && s.label === "Actif" ? (
                    <Button variant="ghost-danger" size="sm" onClick={async () => { await revokeLink(l.id); await load(); }}>
                      Révoquer
                    </Button>
                  ) : null}
                </td>
              </tr>
            );
          })}
          {links.length === 0 ? (
            <tr>
              <td colSpan={6} className="table__empty">
                Aucun lien pour ce dossier.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <Modal open={created !== null} title="Lien créé" onClose={() => setCreated(null)}>
        <p>Transmettez ce lien au destinataire. Il ne sera plus affiché ensuite ; le code de vérification lui parviendra par courriel.</p>
        <p className="liens__url">
          <code>{created}</code>
        </p>
        <Button
          variant="primary"
          onClick={async () => {
            await navigator.clipboard?.writeText(created ?? "");
            push("success", "Lien copié");
          }}
        >
          Copier le lien
        </Button>
      </Modal>
    </div>
  );
}
